import { IntegrationEnhanced } from "@/api/integrations";
import { RequestAccessDialog } from "@/components/RequestAccessDialog";
import { cn } from "@/lib/utils";
import { ConnectionActionType, ConnectionCard } from "@cohesive/ui-components";
import { CheckCircle, ChevronDown, LucideIcon, Plug } from "lucide-react";
import React, { useState } from "react";

interface IntegrationSectionProps {
  title: string;
  integrations: IntegrationEnhanced[];
  icon: LucideIcon;
  iconColorClasses: string;
  connectedIntegrationIds: Set<string>;
  handleIntegrationClick: (integration: IntegrationEnhanced) => void;
  handleIntegrationConnect: (integration: IntegrationEnhanced) => Promise<void>;
  handleIntegrationManage: (integrationId: string) => void;
  getActionType: (
    integration: IntegrationEnhanced,
    sectionTitle?: string,
  ) => ConnectionActionType;
  isConnectedSection?: boolean;
}

const IntegrationSection: React.FC<IntegrationSectionProps> = ({
  title,
  integrations,
  icon: Icon,
  iconColorClasses,
  connectedIntegrationIds,
  handleIntegrationClick,
  handleIntegrationConnect,
  handleIntegrationManage,
  getActionType,
  isConnectedSection = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [requestAccessDialogOpen, setRequestAccessDialogOpen] = useState(false);
  const [selectedIntegrationForAccess, setSelectedIntegrationForAccess] =
    useState<IntegrationEnhanced | null>(null);

  // Enhanced request access handler that shows the dialog
  const handleRequestAccessWithDialog = (integration: IntegrationEnhanced) => {
    setSelectedIntegrationForAccess(integration);
    setRequestAccessDialogOpen(true);
  };

  const getDescription = () => {
    const count = integrations.length;
    const singular = count === 1 ? "integration" : "integrations";

    if (isConnectedSection) {
      return `${count} ${singular} connected`;
    }

    switch (title.toLowerCase()) {
      case "available":
        return `${count} ${singular} available to connect`;
      case "request access":
        return `${count} ${singular} available to request`;
      default:
        return `${count} ${singular}`;
    }
  };

  return (
    <div>
      <div
        className="flex items-center justify-between mb-6 cursor-pointer group"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-12 h-12 rounded-xl ${iconColorClasses} flex items-center justify-center shadow-sm`}
          >
            <div
              className={
                Icon === CheckCircle
                  ? "text-lc-active"
                  : Icon === Plug
                    ? "text-teal"
                    : "text-plum"
              }
            >
              <Icon className="h-6 w-6" />
            </div>
          </div>
          <div>
            <h2 className="text-xl font-semibold">{title}</h2>
            <p className="text-sm text-muted-foreground">{getDescription()}</p>
          </div>
        </div>
        <div className="text-muted-foreground group-hover:text-foreground transition-colors">
          <ChevronDown
            className={cn(
              "h-5 w-5 transition-transform duration-200",
              !isExpanded && "-rotate-90",
            )}
          />
        </div>
      </div>
      {isExpanded && (
        <div
          className={cn(
            "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
            isConnectedSection && "grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
          )}
        >
          {integrations.map((integration) => {
            const isConnected = connectedIntegrationIds.has(integration.id);
            const actionType = getActionType(integration, title);

            if (integration.status === "discoverable") {
              return (
                <ConnectionCard
                  key={integration.id}
                  integration={integration}
                  isConnected={isConnected}
                  actionType={actionType}
                  onClick={() => handleRequestAccessWithDialog(integration)}
                  onConnect={() => handleRequestAccessWithDialog(integration)}
                  onManage={() => handleRequestAccessWithDialog(integration)}
                  onRequestAccess={() =>
                    handleRequestAccessWithDialog(integration)
                  }
                />
              );
            }

            return (
              <ConnectionCard
                key={integration.id}
                integration={integration}
                isConnected={isConnected}
                actionType={actionType}
                onClick={() => handleIntegrationClick(integration)}
                onConnect={() => handleIntegrationConnect(integration)}
                onManage={() => handleIntegrationManage(integration.id)}
                onRequestAccess={() =>
                  handleRequestAccessWithDialog(integration)
                }
              />
            );
          })}
        </div>
      )}

      {/* Request Access Dialog */}
      {selectedIntegrationForAccess && (
        <RequestAccessDialog
          open={requestAccessDialogOpen}
          onOpenChange={setRequestAccessDialogOpen}
          integration={selectedIntegrationForAccess}
        />
      )}
    </div>
  );
};

export default IntegrationSection;
